import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface FaqItem {
  q: string;
  a: string;
}

const SECTIONS: { heading: string; items: FaqItem[] }[] = [
  {
    heading: 'Getting started',
    items: [
      { q: 'Is RideSaathi free to use?', a: 'Yes — every driver gets free ride unlocks every month automatically. No credit card or payment is required to create an account and start browsing.' },
      { q: 'How do I create an account?', a: 'Click "Get started free" on the homepage or at the top of any page. Fill in your name, email and password — you\'ll be browsing live rides within 60 seconds.' },
      { q: 'What cities are covered?', a: 'We currently serve Delhi NCR, Gurugram, Noida, Faridabad, Ghaziabad, Jaipur, and Chandigarh. We are expanding every month — check the live feed to see if your city has active listings.' },
    ],
  },
  {
    heading: 'Rides & unlocks',
    items: [
      { q: 'What is an "unlock"?', a: 'Every ride request shows the route, timing, and area for free. Unlocking reveals the full ride details — pickup, drop, fare, and connection info — so you can reach out directly. Each unlock uses one credit from your plan.' },
      { q: 'Do unused unlock credits roll over?', a: 'Free, Starter and Pro credits reset at the start of each calendar month and do not roll over.' },
      { q: 'How are rides reviewed?', a: 'RideSaathi organizes ride requirements from authorized sources and verified contributors. Duplicates, irrelevant posts, expired listings, and non-ride messages are filtered before appearing in the feed.' },
      { q: 'Can I get a refund on an unlock?', a: 'If the ride details turn out to be invalid (wrong number, inactive), contact our support within 24 hours and we will credit one unlock back to your account.' },
    ],
  },
  {
    heading: 'Plans & billing',
    items: [
      { q: 'How does billing work?', a: 'Plans are billed monthly. You can upgrade, downgrade or cancel at any time from your dashboard. Cancellation takes effect at the end of the current billing period.' },
      { q: 'Which payment methods do you accept?', a: 'We accept UPI, debit/credit cards, and net banking via Razorpay. All prices are in Indian Rupees (INR).' },
      { q: 'What are the paid plans?', a: 'Starter is Rs 79 per month for 50 ride unlocks. Pro is Rs 149 per month for 100 ride unlocks.' },
      { q: 'Can I upgrade mid-month?', a: 'Yes. Upgrading takes effect immediately and your new unlock limit is available right away. We prorate the charge for the remaining days of the billing cycle.' },
    ],
  },
  {
    heading: 'Referrals',
    items: [
      { q: 'How does the referral programme work?', a: 'Share your unique referral link from your dashboard. When a new driver signs up using your link, both of you earn bonus unlock credits automatically — no manual claiming required.' },
      { q: 'How many bonus unlocks do I get per referral?', a: 'You earn 3 bonus unlocks for each friend who signs up and completes their first unlock. There is no cap on the number of referrals.' },
    ],
  },
  {
    heading: 'Technical & support',
    items: [
      { q: 'How do I enable push notifications?', a: 'After logging in, the app will ask for notification permission. Allow it and you will receive a push alert when new rides arrive. Your saved filters rank the best matches when you open the feed.' },
      { q: 'Who do I contact if I have a problem?', a: 'Email us at support@ridesaathi.in or use the Contact page. We typically respond within a few hours on business days.' },
    ],
  },
];

const FaqRow: React.FC<FaqItem> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, cursor: 'pointer', '&:hover': { color: '#07182d' } }}
      >
        <Typography variant="subtitle1" fontWeight={600} pr={2}>{q}</Typography>
        {open ? <ExpandLessIcon sx={{ flexShrink: 0 }} /> : <ExpandMoreIcon sx={{ flexShrink: 0 }} />}
      </Box>
      <Collapse in={open}>
        <Typography variant="body2" color="text.secondary" pb={2} lineHeight={1.75}>{a}</Typography>
      </Collapse>
      <Divider />
    </Box>
  );
};

const FAQ: React.FC = () => {
  const { token } = useAuth();
  const isLoggedIn = Boolean(token);

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg,#061b31,#0b3156)', py: { xs: 10, md: 12 }, px: 3, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ fontSize: { xs: '2rem', md: '2.8rem' } }}>
            Frequently asked questions
          </Typography>
          <Typography variant="h6" color="rgba(226,232,240,0.7)" maxWidth={500} fontWeight={400}>
            Everything you need to know about RideSaathi. Can't find your answer?{' '}
            <Box component={RouterLink} to="/contact" sx={{ color: '#ffc928', textDecoration: 'underline' }}>
              Contact us
            </Box>
            .
          </Typography>
        </Stack>
      </Box>

      {/* FAQ sections */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Grid container spacing={4} sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 3, md: 6 } }}>
          {SECTIONS.map((section) => (
            <Grid item xs={12} key={section.heading}>
              <Typography variant="overline" fontWeight={700} color="text.secondary" letterSpacing={1.5} display="block" mb={1}>
                {section.heading}
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 3, px: 3, border: '1px solid rgba(15,23,42,0.07)' }}>
                {section.items.map((item) => (
                  <FaqRow key={item.q} {...item} />
                ))}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'white', textAlign: 'center', px: 3 }}>
        <Stack alignItems="center" spacing={2.5}>
          <Typography variant="h4" fontWeight={800}>Still have questions?</Typography>
          <Typography variant="body1" color="text.secondary">Our team is happy to help. Reach out anytime.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button component={RouterLink} to="/contact" variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, px: 4 }}>
              Contact support
            </Button>
            <Button component={RouterLink} to={isLoggedIn ? '/rides' : '/signup'} variant="outlined" size="large">
              {isLoggedIn ? 'Browse live rides' : 'Create free account'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default FAQ;
